import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { format, parseISO } from "date-fns";
import {
  useUpdatePayrollEntryMutation,
  useGetPayrollQuery,
} from "@/store/payrolls/apis";
import { Loading } from "@/components/Loading";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { CheckIcon } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowLeft } from "@fortawesome/free-solid-svg-icons";
import { faPenToSquare } from "@awesome.me/kit-9096fb77ac/icons/classic/regular";

interface EditableFieldProps {
  value: number;
  onSave: (value: number) => void;
}

const EditableField: React.FC<EditableFieldProps> = ({ value, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value.toString());

  const handleSave = () => {
    onSave(parseFloat(editValue));
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex justify-end items-center gap-1">
        <Input
          type="number"
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          className="w-20"
          onFocus={(e) => e.target.select()}
        />
        <Button
          size="sm"
          className="bg-green-600 hover:bg-green-700 text-white px-2 py-1"
          onClick={handleSave}
        >
          <CheckIcon className="h-4 w-4" />
        </Button>
        {/* <Button
          size="sm"
          variant="destructive"
          className="bg-red-600 hover:bg-red-700 text-white px-2 py-1"
          onClick={() => setIsEditing(false)}
        >
          <XIcon className="h-4 w-4" />
        </Button> */}
      </div>
    );
  }

  return (
    <div
      className="w-auto cursor-pointer hover:bg-gray-100 py-1 text-right flex justify-end items-center gap-1"
      onClick={() => setIsEditing(true)}
    >
      <FontAwesomeIcon icon={faPenToSquare} className="h-4 w-4" />
      {Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
      }).format(value)}
    </div>
  );
};

const PayrollDetail = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { toast } = useToast();
  const {
    data: payroll,
    isLoading,
    refetch,
  } = useGetPayrollQuery({
    id: parseInt(id || "0"),
  });
  const [updatePayrollEntry] = useUpdatePayrollEntryMutation();

  const handleUpdateField = async (
    entryId: number,
    field: "tips" | "bonuses" | "deductions",
    value: number
  ) => {
    try {
      await updatePayrollEntry({
        payrollId: parseInt(id || "0"),
        entryId,
        data: {
          [field]: value,
        },
      }).unwrap();

      toast({
        title: "Success",
        description: "Updated successfully",
      });

      refetch();
    } catch {
      toast({
        title: "Error",
        description: "Failed to update",
        variant: "destructive",
      });
    }
  };

  if (isLoading)
    return (
      <div className="p-6">
        <Loading />
      </div>
    );
  if (!payroll) return <div className="p-6">Payroll not found</div>;

  return (
    <>
      <div className="p-6 max-w-screen-xl">
        <div className="mb-6">
          <button
            onClick={() => {
              navigate("/payroll", { state: { refresh: true } });
            }}
            className="text-sm font-semibold flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="h-4 w-4" />
            All Payrolls
          </button>
          <div className="flex justify-between items-baseline">
            <div>
              <h1 className="mt-2 text-xl font-bold">Payroll</h1>
              <h3 className="text-base">
                {format(parseISO(payroll.start_date), "MMM d, yyyy")} -{" "}
                {format(parseISO(payroll.end_date), "MMM d, yyyy")}
              </h3>
            </div>
            <div>
              <h2 className="text-2xl font-bold">
                {Intl.NumberFormat("en-US", {
                  style: "currency",
                  currency: "USD",
                }).format(payroll.pay)}
              </h2>
              <p className="text-base text-gray-700">Total Payroll</p>
            </div>
          </div>
        </div>

        <div className="text-sm">
          <div className="grid grid-cols-8 gap-4 p-4 font-medium border-b uppercase text-xs text-gray-700 text-right">
            <div className="col-span-1 text-left">Name</div>
            <div>Rate</div>
            <div>Hours</div>
            <div>Hourly Pay</div>
            <div>Tips</div>
            <div>Bonus</div>
            <div>Deductions</div>
            <div>Net Pay</div>
          </div>
          {payroll.payroll_entries.map((entry) => (
            <div>
              <div
                key={entry.id}
                className="grid grid-cols-8 gap-4 px-4 py-2 border-b hover:bg-gray-50 flex items-center text-right"
              >
                <div className="col-span-1 text-left font-semibold">
                  <Link
                    to={`/payroll/${payroll.id}/entry/${entry.id}`}
                    className="font-semibold"
                  >
                    {entry.user.name}
                  </Link>
                </div>
                <div>
                  {Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(entry.hourly_rate)}
                  /h
                </div>
                <div>{entry.hours_worked}</div>
                <div>
                  {Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(entry.hourly_pay)}
                </div>
                <EditableField
                  value={entry.tips}
                  onSave={(value) => handleUpdateField(entry.id, "tips", value)}
                />
                <EditableField
                  value={entry.bonuses}
                  onSave={(value) =>
                    handleUpdateField(entry.id, "bonuses", value)
                  }
                />
                <EditableField
                  value={entry.deductions}
                  onSave={(value) =>
                    handleUpdateField(entry.id, "deductions", value)
                  }
                />
                <div>
                  {Intl.NumberFormat("en-US", {
                    style: "currency",
                    currency: "USD",
                  }).format(entry.net_pay)}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

export default PayrollDetail;
